import { useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import {
  EXTENDED_METADATA_VALUES_OPTIONS,
  TEST_MANAGEMENT_TESTS,
} from '@elementary/common-ui/constants/queryKeys';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/api';
import useTestOverviewData from './useTestOverviewData';

const useTestMetadataUpdate = ({ isCloudTest }: { isCloudTest?: boolean }) => {
  const selectedEnvId = useSelectedEnv();
  const { selectedId } = useSidePanelParams();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const { invalidate: invalidateTestOverview } = useTestOverviewData();

  const isSavingOwnersRef = useRef(false);
  const pendingOwnersRef = useRef<string[] | null>(null);

  const isSavingTagsRef = useRef(false);
  const pendingTagsRef = useRef<string[] | null>(null);

  const { data: metadataOptions } = useQuery(
    [EXTENDED_METADATA_VALUES_OPTIONS, selectedEnvId],
    () => api.fetchExtendedMetadataValueOptions(selectedEnvId!),
    { enabled: isCloudTest && !!selectedEnvId },
  );

  const { mutateAsync: doUpdateOwners, isLoading: isUpdatingOwners } =
    useMutation(
      (owners: string[]) =>
        api.updateTest(selectedEnvId!, selectedId!, { owners }),
      {
        onSuccess: () => {
          invalidateTestOverview();
          queryClient.invalidateQueries([TEST_MANAGEMENT_TESTS, selectedEnvId]);
        },
        onError: () => {
          openSnackbar({
            message: 'Failed to update owners.',
            variant: 'error',
          });
        },
      },
    );

  const { mutateAsync: doUpdateTags } = useMutation(
    (tags: string[]) => api.updateTest(selectedEnvId!, selectedId!, { tags }),
    {
      onSuccess: () => {
        invalidateTestOverview();
      },
      onError: () => {
        openSnackbar({
          message: 'Failed to update tags.',
          variant: 'error',
        });
      },
    },
  );

  const updateOwners = useCallback(
    async (owners: string[]) => {
      if (isSavingOwnersRef.current) {
        pendingOwnersRef.current = owners;
        return;
      }

      isSavingOwnersRef.current = true;
      try {
        await doUpdateOwners(owners);
      } finally {
        isSavingOwnersRef.current = false;
        if (pendingOwnersRef.current !== null) {
          const pending = pendingOwnersRef.current;
          pendingOwnersRef.current = null;
          updateOwners(pending);
        }
      }
    },
    [doUpdateOwners],
  );

  const updateTags = useCallback(
    async (tags: string[]) => {
      if (isSavingTagsRef.current) {
        pendingTagsRef.current = tags;
        return;
      }

      isSavingTagsRef.current = true;
      try {
        await doUpdateTags(tags);
      } finally {
        isSavingTagsRef.current = false;
        if (pendingTagsRef.current !== null) {
          const pending = pendingTagsRef.current;
          pendingTagsRef.current = null;
          updateTags(pending);
        }
      }
    },
    [doUpdateTags],
  );

  return {
    metadataOptions,
    updateOwners,
    updateTags,
    isUpdatingOwners,
  };
};

export default useTestMetadataUpdate;
